'use client';
/* eslint @typescript-eslint/no-var-requires: "off" */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import bannerStyles from './bannerStyles.module.css';

import { useScreenSize } from '@/hooks/useScreenSizeDecodo';
import { cn } from '@/lib/utils';

import { getWrapperMaxWidth } from '@/features/utils/utils';

import { BannerContent } from './BannerContent';
import { TopBannerProps } from './types';

import IconClose from '~/icons/x-close.svg';

const BANNER_DISMISS_KEY = 'topBannerDismissed';

export const TopBanner = ({ bannerContent }: TopBannerProps) => {
  const [isTopBannerOpen, setIsTopBannerOpen] = useState<boolean | undefined>(
    undefined
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const multipleBanners = Array.isArray(bannerContent)
    ? bannerContent
    : [bannerContent];

  const { isDesktop } = useScreenSize();

  const hasEndDate = multipleBanners.some((banner) => !!banner.endDate);
  const heightIfEndDateExist = hasEndDate ? '48px' : '40px';
  const bannerMinHeightClass = hasEndDate ? 'min-h-[48px]' : 'min-h-[40px]';

  useEffect(() => {
    const dismissedAt = localStorage.getItem(BANNER_DISMISS_KEY);

    if (!dismissedAt) {
      setIsTopBannerOpen(true);
      return;
    }

    const dismissedTime = parseInt(dismissedAt, 10);
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    if (isNaN(dismissedTime) || now - dismissedTime >= oneDay) {
      localStorage.removeItem(BANNER_DISMISS_KEY);
      setIsTopBannerOpen(true);
    } else {
      setIsTopBannerOpen(false);
    }
  }, []);

  useEffect(() => {
    const header = document.getElementById('header');
    const breadcrumbs = document.getElementById('breadcrumbs');

    const shouldOffset =
      isDesktop && isTopBannerOpen && multipleBanners.length > 0;

    const newTop = isDesktop && shouldOffset ? heightIfEndDateExist : '0';

    if (header) {
      header.style.top = newTop;
    }

    if (breadcrumbs) {
      breadcrumbs.style.marginTop = newTop;
    }
  }, [
    isDesktop,
    isTopBannerOpen,
    heightIfEndDateExist,
    multipleBanners.length,
  ]);

  useEffect(() => {
    if (!isTopBannerOpen) return;

    const interval = setInterval(() => {
      if (!isHovered) {
        setCurrentIndex((prev) => (prev + 1) % multipleBanners.length);
      }
    }, 7000);

    return () => clearInterval(interval);
  }, [isTopBannerOpen, isHovered, multipleBanners.length]);

  const handleClose = () => {
    setIsTopBannerOpen(false);
    localStorage.setItem(BANNER_DISMISS_KEY, Date.now().toString());
  };

  if (isTopBannerOpen === undefined) return null;
  if (!isTopBannerOpen) return null;
  if (multipleBanners.length === 0) return null;

  return (
    <div
      data-tracker-section='top-banner'
      className={cn(
        'bg-brand-950 headerLg:justify-center headerLg:px-10 fixed top-0 z-50 mx-auto hidden w-full justify-end p-5 shadow-sm md:flex md:py-0',
        bannerStyles.banner
      )}
    >
      <div
        className={cn(
          `relative flex w-full grow items-center justify-end`,
          bannerMinHeightClass,
          getWrapperMaxWidth('mid')
        )}
      >
        <AnimatePresence mode='wait'>
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 1 }}
            exit={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
            className='absolute inset-0 flex justify-center'
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
          >
            <BannerContent {...multipleBanners[currentIndex]} />
          </motion.div>
        </AnimatePresence>

        <div
          className='absolute right-3 top-5 z-10 cursor-pointer sm:right-5 md:static md:ml-6 xl:ml-[110px]'
          onClick={handleClose}
          data-tracker='close-promo-banner'
        >
          <div className='text-brand-300 h-6 w-6'>
            <IconClose />
          </div>
        </div>
      </div>
    </div>
  );
};
